import { useMemo, useState } from 'react';
import { Badge, Button, Group, Modal, ScrollArea, Stack, Text, TextInput, UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconBuilding, IconCheck, IconChevronDown, IconSearch, IconShieldLock } from '@tabler/icons-react';
import { useOrg } from '@/hooks/use-org';
import type { Org } from '@/contexts/org';

function abbreviate(name: string) {
  const words = name.trim().split(/\s+/);

  if (words.length === 1) return (words[0] ?? '').slice(0, 3).toUpperCase();

  return words
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase();
}

function matches(organizations: Org[], search: string) {
  return organizations.filter((o) => o.name.toLowerCase().includes(search.toLowerCase()));
}

function OrgOption({
  organization,
  isCurrent,
  onSelect,
}: {
  organization: Org;
  isCurrent: boolean;
  onSelect: (organizationId: string) => void;
}) {
  return (
    <UnstyledButton
      onClick={() => onSelect(organization.id)}
      py={8}
      px={12}
      style={{ borderRadius: 'var(--mantine-radius-sm)' }}
      className="hover:bg-[var(--mantine-color-gray-light-hover)]"
    >
      <div className="flex items-center justify-between">
        <Text size="sm" truncate>
          {organization.name}
        </Text>
        {isCurrent && <IconCheck size={16} color="var(--mantine-color-blue-filled)" />}
      </div>
    </UnstyledButton>
  );
}

export function OrgSwitcher() {
  const { organizations, platformOrganizations, currentOrg, setCurrentOrg, isPlatformOperatorView } = useOrg();
  const [opened, { open, close }] = useDisclosure(false);
  const [search, setSearch] = useState('');

  const filteredMemberships = useMemo(() => matches(organizations, search), [organizations, search]);
  const filteredPlatform = useMemo(() => matches(platformOrganizations, search), [platformOrganizations, search]);

  const select = async (organizationId: string) => {
    await setCurrentOrg(organizationId);
    close();
    setSearch('');
  };

  if (organizations.length + platformOrganizations.length <= 1) return null;

  return (
    <>
      <Button
        variant="subtle"
        color={isPlatformOperatorView ? 'grape' : 'gray'}
        size="compact-sm"
        rightSection={<IconChevronDown size={14} />}
        leftSection={isPlatformOperatorView ? <IconShieldLock size={16} /> : <IconBuilding size={16} />}
        onClick={open}
      >
        <Text size="sm">{currentOrg ? abbreviate(currentOrg.name) : 'Select org'}</Text>
      </Button>

      <Modal
        opened={opened}
        onClose={() => {
          close();
          setSearch('');
        }}
        title="Switch Organization"
        size="sm"
      >
        <Stack gap="sm">
          <TextInput
            placeholder="Search organizations..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            autoFocus
          />

          <ScrollArea.Autosize mah={400}>
            <Stack gap="xs">
              {filteredMemberships.length === 0 && filteredPlatform.length === 0 && (
                <Text c="dimmed" ta="center" py="md" size="sm">
                  No organizations found
                </Text>
              )}

              {filteredMemberships.length > 0 && (
                <Stack gap={4}>
                  <Text size="xs" fw={700} c="dimmed" tt="uppercase" px={12} style={{ letterSpacing: '0.08em' }}>
                    Your organizations
                  </Text>
                  {filteredMemberships.map((org) => (
                    <OrgOption
                      key={org.id}
                      organization={org}
                      isCurrent={org.id === currentOrg?.id}
                      onSelect={(id) => void select(id)}
                    />
                  ))}
                </Stack>
              )}

              {filteredPlatform.length > 0 && (
                <Stack
                  gap={4}
                  pt="xs"
                  style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}
                  bg="var(--mantine-color-grape-light)"
                >
                  <Group gap={6} px={12} pt={6}>
                    <IconShieldLock size={14} color="var(--mantine-color-grape-filled)" />
                    <Text size="xs" fw={700} c="grape" tt="uppercase" style={{ letterSpacing: '0.08em' }}>
                      All organizations
                    </Text>
                    <Badge size="xs" color="grape" variant="light">
                      platform operator
                    </Badge>
                  </Group>
                  {filteredPlatform.map((org) => (
                    <OrgOption
                      key={org.id}
                      organization={org}
                      isCurrent={org.id === currentOrg?.id}
                      onSelect={(id) => void select(id)}
                    />
                  ))}
                </Stack>
              )}
            </Stack>
          </ScrollArea.Autosize>
        </Stack>
      </Modal>
    </>
  );
}
